import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Users, Clock } from "lucide-react";

/**
 * Éditeur de liste d'intervenants (mécaniciens + heures passées) sur un OR.
 *
 * Props :
 *   - intervenants : Array<{mecanicien_id: string, heures: number}>
 *   - setIntervenants : (next) => void
 *   - mecaniciens : Array<{id, label}>  (liste de tous les mécaniciens dispo)
 *   - compact (bool) : version réduite pour le dialog de finalisation
 *
 * Affiche le total des heures et précise que le CA sera réparti au prorata.
 */
export default function IntervenantsEditor({ intervenants, setIntervenants, mecaniciens, compact = false }) {
  const list = intervenants || [];

  const updateAt = (idx, patch) => {
    setIntervenants(list.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };
  const removeAt = (idx) => setIntervenants(list.filter((_, i) => i !== idx));
  const addRow = () => {
    // _localId : identifiant stable pour la clé React, indépendant de l'index
    // (évite la perte de focus / état lors de réordonnancement ou suppression).
    const _localId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setIntervenants([...list, { _localId, mecanicien_id: "", heures: 0 }]);
  };

  const totalH = list.reduce((acc, x) => acc + (parseFloat(x.heures) || 0), 0);
  const usedIds = new Set(list.map((x) => x.mecanicien_id).filter(Boolean));

  return (
    <div className="space-y-2" data-testid="intervenants-editor">
      {!compact && (
        <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <Users className="w-3.5 h-3.5" /> Mécaniciens intervenus
        </Label>
      )}

      <div className="space-y-1.5">
        {list.length === 0 && (
          <div className="text-xs text-gray-400 italic py-2">
            Aucun intervenant. Ajoutez les mécaniciens qui ont travaillé sur cet OR.
          </div>
        )}
        {list.map((x, idx) => (
          <div key={x._localId || x.mecanicien_id || `row-${idx}`} className="flex items-center gap-2" data-testid={`intervenant-row-${idx}`}>
            <Select
              value={x.mecanicien_id || ""}
              onValueChange={(v) => updateAt(idx, { mecanicien_id: v })}
            >
              <SelectTrigger className="flex-1 h-9" data-testid={`intervenant-mec-${idx}`}>
                <SelectValue placeholder="Choisir un mécanicien" />
              </SelectTrigger>
              <SelectContent>
                {mecaniciens.map((m) => (
                  <SelectItem
                    key={m.id}
                    value={m.id}
                    disabled={usedIds.has(m.id) && m.id !== x.mecanicien_id}
                  >
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Input
                type="number" step="0.25" min="0"
                value={x.heures ?? ""}
                onChange={(e) => updateAt(idx, { heures: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                onBlur={() => { if (x.heures === "" || isNaN(x.heures)) updateAt(idx, { heures: 0 }); }}
                placeholder="0"
                className="h-9 w-24 pr-7 text-right"
                data-testid={`intervenant-h-${idx}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">h</span>
            </div>
            <Button
              type="button" size="icon" variant="ghost"
              onClick={() => removeAt(idx)}
              className="h-9 w-9 text-red-600 hover:bg-red-50 flex-shrink-0"
              data-testid={`intervenant-del-${idx}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button
          type="button" size="sm" variant="outline"
          onClick={addRow}
          disabled={mecaniciens.length === 0 || usedIds.size >= mecaniciens.length}
          data-testid="intervenant-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un mécanicien
        </Button>
        {list.length > 0 && (
          <div className="text-xs font-mono inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            Total : <b className="text-gray-800">{totalH.toFixed(2)} h</b>
          </div>
        )}
      </div>

      {list.length > 1 && (
        <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded p-2">
          💡 Le CA généré (HT) sera réparti <b>au prorata des heures</b> dans les statistiques par mécanicien.
        </div>
      )}
    </div>
  );
}
